/**
 * Install App banner + offline pill.
 *
 * - Shows a dismissible "Install Altara Wallet" banner when the browser
 *   fires `beforeinstallprompt` (Chromium / Android). Dismissal persists
 *   for 7 days per device via localStorage.
 * - Always renders a subtle "Offline — changes will sync" chip when
 *   `navigator.onLine === false`.
 * - Renders a one-time iOS "Add to Home Screen" helper card for iOS Safari
 *   (which does NOT fire beforeinstallprompt) — user-dismissible + 7-day snooze.
 */
import { useEffect, useState } from "react";
import { Download, WifiOff, X, Share } from "lucide-react";
import { useInstallPrompt, useOnlineStatus } from "@/lib/pwa";

const DISMISS_KEY = "altara:install-prompt-dismissed-until";
const IOS_KEY = "altara:ios-a2hs-dismissed-until";
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

const isIos = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isAppleTouch = /iPad|iPhone|iPod/.test(ua);
  const isNotStandalone = !window.matchMedia?.("(display-mode: standalone)").matches && window.navigator?.standalone !== true;
  return isAppleTouch && isNotStandalone;
};

const snoozed = (key) => {
  try {
    const until = Number(localStorage.getItem(key) || 0);
    return until > Date.now();
  } catch {
    return false;
  }
};

const snooze = (key) => {
  try {
    localStorage.setItem(key, String(Date.now() + SEVEN_DAYS_MS));
  } catch {
    /* ignore quota */
  }
};

export default function InstallAppPrompt() {
  const { canInstall, installed, promptInstall } = useInstallPrompt();
  const online = useOnlineStatus();
  const [dismissed, setDismissed] = useState(() => snoozed(DISMISS_KEY));
  const [iosDismissed, setIosDismissed] = useState(() => snoozed(IOS_KEY));
  const [showIos, setShowIos] = useState(false);

  useEffect(() => {
    // Only advertise A2HS on iOS if the user has spent at least ~10s in the wallet.
    if (!isIos() || installed || iosDismissed) return;
    const t = setTimeout(() => setShowIos(true), 10_000);
    return () => clearTimeout(t);
  }, [installed, iosDismissed]);

  const onInstall = async () => {
    const res = await promptInstall();
    if (res?.outcome === "dismissed") {
      snooze(DISMISS_KEY);
      setDismissed(true);
    }
  };

  const onDismiss = () => {
    snooze(DISMISS_KEY);
    setDismissed(true);
  };

  const onIosDismiss = () => {
    snooze(IOS_KEY);
    setIosDismissed(true);
    setShowIos(false);
  };

  return (
    <>
      {/* Offline pill — persistent while offline */}
      {!online && (
        <div
          data-testid="offline-chip"
          className="fixed z-40 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-100 border border-amber-300 shadow-md"
          style={{ bottom: "88px", left: "50%", transform: "translateX(-50%)" }}
        >
          <WifiOff className="w-3.5 h-3.5 text-amber-800" />
          <span className="text-[11px] font-semibold text-amber-900 tracking-wide">Offline — changes will sync</span>
        </div>
      )}

      {/* Install prompt (Android / desktop Chromium) */}
      {canInstall && !installed && !dismissed && (
        <div
          data-testid="install-app-banner"
          className="fixed z-40 max-w-md mx-auto left-4 right-4 lg:left-auto lg:right-6 lg:bottom-6 rounded-2xl border shadow-lg bg-white"
          style={{ bottom: "88px", borderColor: "#C9A24A55" }}
        >
          <div className="flex items-start gap-3 p-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "#0A1F44" }}>
              <Download className="w-5 h-5 text-white" strokeWidth={2.2} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold" style={{ color: "#0A1F44" }}>Install Altara Wallet</div>
              <p className="text-[12px] text-slate-600 leading-snug mt-0.5">
                Faster access to your Trust Wallet, offline support, and Life Journey progress on your home screen.
              </p>
              <div className="flex items-center gap-2 mt-2.5">
                <button
                  data-testid="install-app-btn"
                  onClick={onInstall}
                  className="text-[12px] font-semibold px-3 py-1.5 rounded-full text-white"
                  style={{ background: "#0A1F44" }}
                >
                  Install
                </button>
                <button
                  data-testid="install-app-dismiss"
                  onClick={onDismiss}
                  className="text-[12px] font-medium px-3 py-1.5 rounded-full text-slate-500 hover:bg-slate-50"
                >
                  Not now
                </button>
              </div>
            </div>
            <button onClick={onDismiss} aria-label="Dismiss" className="p-1 -mr-1 -mt-1 rounded hover:bg-slate-100 text-slate-400">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* iOS Add-to-Home-Screen helper card (Safari can't be prompted) */}
      {showIos && !installed && !iosDismissed && (
        <div
          data-testid="ios-a2hs-banner"
          className="fixed z-40 left-4 right-4 lg:hidden rounded-2xl border shadow-lg bg-white"
          style={{ bottom: "88px", borderColor: "#C9A24A55" }}
        >
          <div className="flex items-start gap-3 p-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "#0A1F44" }}>
              <Share className="w-5 h-5 text-white" strokeWidth={2.2} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold" style={{ color: "#0A1F44" }}>Add Altara to Home Screen</div>
              <p className="text-[12px] text-slate-600 leading-snug mt-0.5">
                Tap the <span className="font-semibold">Share</span> icon in Safari, then <span className="font-semibold">Add to Home Screen</span>.
              </p>
              <button
                data-testid="ios-a2hs-dismiss"
                onClick={onIosDismiss}
                className="text-[12px] font-medium mt-2 text-slate-500 hover:text-slate-700"
              >
                Got it
              </button>
            </div>
            <button onClick={onIosDismiss} aria-label="Dismiss" className="p-1 -mr-1 -mt-1 rounded hover:bg-slate-100 text-slate-400">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
